import {
  Badge,
  Checkbox,
  Flex,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useTranslation } from 'react-i18next';

export function AddVariantSupplierOptionsPanel({
  options,
  loading,
  selectedIds,
  onChange,
}: {
  options: CompositionGroup[];
  loading: boolean;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { t } = useTranslation();

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );
  };

  return (
    <Flex direction="column" flex={1} gap={4} minW={0}>
      <Flex align="center" justify="space-between" gap={4}>
        <Heading fontSize="xl">{t('navigation.versions')}</Heading>
        <Text
          color="entityDirectory.mutedText"
          fontSize="xs"
          whiteSpace="nowrap"
        >
          {t('variant.supplier.addModal.selectedOptions', {
            selected: selectedIds.length,
            total: options.length,
          })}
        </Text>
      </Flex>
      <Text color="entityDirectory.mutedText" fontSize="sm">
        {t('variant.supplier.addModal.optionsDescription')}
      </Text>
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <Flex direction="column" gap={2}>
          {options.map((option) => {
            const selected = selectedIds.includes(option.id);
            return (
              <Flex
                key={option.id}
                as="label"
                align="center"
                gap={3}
                py={3.5}
                px={4}
                cursor={option.isReady ? 'pointer' : 'not-allowed'}
                opacity={option.isReady ? 1 : 0.72}
                border="1px solid"
                borderColor="entityDirectory.border"
              >
                <Checkbox
                  isChecked={selected}
                  isDisabled={!option.isReady}
                  onChange={() => toggle(option.id)}
                  borderRadius={0}
                />
                <Flex direction="column" flex={1} minW={0} gap={0.5}>
                  <Text fontWeight="semibold" noOfLines={1}>
                    {option.version.toString().padStart(2, '0')} ·{' '}
                    {option.note ?? t('general.option')}
                  </Text>
                  <Text
                    color={
                      option.isReady
                        ? 'entityDirectory.mutedText'
                        : 'orange.300'
                    }
                    _light={{
                      color: option.isReady
                        ? 'entityDirectory.mutedText'
                        : 'orange.700',
                    }}
                    fontSize="xs"
                  >
                    {option.isReady
                      ? selected
                        ? t('variant.supplier.addModal.willCreateVersion')
                        : t('variant.supplier.addModal.willNotCreateVersion')
                      : t('variant.supplier.addModal.availableWhenReady')}
                  </Text>
                </Flex>
                <Badge
                  bg={
                    option.isReady
                      ? 'entityDirectory.successBackground'
                      : 'transparent'
                  }
                  borderRadius={0}
                  color={
                    option.isReady
                      ? 'entityDirectory.successText'
                      : 'orange.300'
                  }
                  _light={{
                    color: option.isReady
                      ? 'entityDirectory.successText'
                      : 'orange.700',
                  }}
                >
                  {option.isReady
                    ? t('variant.supplier.addModal.ready')
                    : t('variant.supplier.addModal.notReady')}
                </Badge>
              </Flex>
            );
          })}
          {options.length === 0 && (
            <Text color="entityDirectory.mutedText" fontSize="sm">
              {t('compositionGroup.option.noOptions')}
            </Text>
          )}
        </Flex>
      )}
      <Text color="entityDirectory.mutedText" fontSize="xs">
        {t('variant.supplier.addModal.noOptionsHint')}
      </Text>
    </Flex>
  );
}
